import { z } from "zod";
import { API_ROUTE_TEMPLATES } from "./api-routes.generated.ts";
import { reducePath, safeErrorName, safeMessage } from "./log-safety.ts";

/**
 * Client telemetry (openspec change `client-telemetry`, spec `client-telemetry`): what the web
 * client reports about its own health, and how each field is reduced. The SPA reduces before
 * queuing (so the recent-errors list shows what would be sent) and the API parses with the same
 * strict schema and reduces again (design D3): a forged or stale client gains nothing.
 */

/** The ingest endpoint, on the page's own origin (no CSP change). */
export const CLIENT_EVENTS_PATH = "/api/client-events";
/** Largest batch body the endpoint reads (`413` past it). */
export const CLIENT_EVENTS_BODY_LIMIT = 16 * 1024;
/** Events per batch. */
export const CLIENT_EVENTS_PER_BATCH = 20;
/** Events one page load sends at most, whatever happens (an error loop included). */
export const CLIENT_EVENTS_PER_PAGE = 100;
/** How often the collector flushes its queue. */
export const CLIENT_EVENTS_FLUSH_MS = 10_000;
/** Share of page loads that report Core Web Vitals. */
export const CLIENT_VITALS_SAMPLE_RATE = 0.1;
/** Stack frames kept per error. */
export const CLIENT_ERROR_FRAMES = 5;
/** Reduced errors the tab keeps in memory for feedback. */
export const RECENT_ERRORS = 10;

/**
 * A route name of the SPA's router (`project.files`, `wiki.page`): lowercase words joined by
 * dots, never an identifier.
 */
export const ROUTE_NAME = /^[a-z]+(?:\.[a-z-]+){0,3}$/;
/** A file of the SPA's own build: `/assets/<name>-<hash>.js`. */
export const OWN_ASSET = /^\/assets\/[A-Za-z0-9_.-]{1,80}-[A-Za-z0-9_-]{6,16}\.js$/;
/** `YYYYMMDDTHHMMSSZ-<sha>` (scripts/build-id.ts), or `dev` outside git. */
export const CLIENT_BUILD_ID = /^(?:\d{8}T\d{6}Z(?:-[0-9A-Za-z.-]{1,40})?|dev)$/;
/** A BCP 47 language tag reduced to language and region. */
const LOCALE = /^[a-z]{2,3}(?:-[A-Z]{2})?$/;

export const EXTERNAL_FRAME = "external";

const routeField = z.string().min(1).max(120);
const frameSchema = z.strictObject({
  file: z.string().min(1).max(120),
  line: z.number().int().min(0).max(10_000_000),
  col: z.number().int().min(0).max(10_000_000),
});
export type ClientFrame = z.infer<typeof frameSchema>;

export const clientErrorEventSchema = z.strictObject({
  kind: z.literal("error"),
  source: z.enum(["window", "promise", "render"]),
  route: routeField,
  name: z.string().min(1).max(64),
  message: z.string().max(400).optional(),
  frames: z.array(frameSchema).max(CLIENT_ERROR_FRAMES),
  /** The same error repeated within the page load (name, message and first frame). */
  count: z.number().int().min(1).max(CLIENT_EVENTS_PER_PAGE),
});

export const DURATION_BUCKETS = ["<1s", "<5s", "<30s", ">=30s"] as const;
export type DurationBucket = (typeof DURATION_BUCKETS)[number];

const requestEventSchema = z.strictObject({
  kind: z.literal("request"),
  route: routeField,
  method: z.enum(["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"]),
  /** The API's route template (`/api/projects/:id/documents`), never the path called. */
  apiRoute: z.string().min(1).max(200),
  /** A 5xx status, or a request that never got an answer. 4xx is never reported. */
  failure: z.union([z.number().int().min(500).max(599), z.enum(["network", "timeout"])]),
  durationBucket: z.enum(DURATION_BUCKETS),
});

export const VITAL_NAMES = ["LCP", "INP", "CLS", "TTFB"] as const;
export type VitalName = (typeof VITAL_NAMES)[number];

const vitalEventSchema = z.strictObject({
  kind: z.literal("vital"),
  route: routeField,
  name: z.enum(VITAL_NAMES),
  /** Milliseconds, unitless for CLS. */
  value: z.number().min(0).max(600_000),
});

const bootEventSchema = z.strictObject({
  kind: z.literal("boot"),
  route: routeField,
});

export const clientEventSchema = z.discriminatedUnion("kind", [
  clientErrorEventSchema,
  requestEventSchema,
  vitalEventSchema,
  bootEventSchema,
]);
export type ClientEvent = z.infer<typeof clientEventSchema>;
export type ClientErrorEvent = z.infer<typeof clientErrorEventSchema>;
export type ClientRequestEvent = z.infer<typeof requestEventSchema>;
export type ClientVitalEvent = z.infer<typeof vitalEventSchema>;

export const BROWSER_FAMILIES = [
  "chrome",
  "edge",
  "firefox",
  "safari",
  "opera",
  "samsung",
  "other",
] as const;
export const OS_FAMILIES = [
  "windows",
  "macos",
  "ios",
  "android",
  "linux",
  "chromeos",
  "other",
] as const;

/** What every event of the page shares: coarse classes, never the raw user agent. */
export const clientContextSchema = z.strictObject({
  browser: z.enum(BROWSER_FAMILIES),
  browserMajor: z.number().int().min(0).max(999).optional(),
  os: z.enum(OS_FAMILIES),
  locale: z.string().max(16).optional(),
  viewport: z.enum(["s", "m", "l"]),
});
export type ClientContext = z.infer<typeof clientContextSchema>;

/**
 * One batch. `build` is for `navigator.sendBeacon`, which cannot send the `x-esi-build-id`
 * header; the header wins when both are present.
 */
export const clientEventBatchSchema = z.strictObject({
  build: z.string().max(64).optional(),
  context: clientContextSchema,
  events: z.array(clientEventSchema).min(1).max(CLIENT_EVENTS_PER_BATCH),
});
export type ClientEventBatch = z.infer<typeof clientEventBatchSchema>;

// ── Reducers (run by the SPA before queuing, and again by the API) ──────────────────────────

/** A route name as is; a path-shaped value reduced to route words and `:id`; else `unknown`. */
export function reduceRoute(route: string): string {
  if (ROUTE_NAME.test(route)) {
    return route;
  }
  return route.startsWith("/") ? reducePath(route.split(/[?#]/)[0] ?? "") : "unknown";
}

const TEMPLATES: ReadonlySet<string> = new Set(API_ROUTE_TEMPLATES);
const TEMPLATE_SEGMENTS = API_ROUTE_TEMPLATES.map((t) => t.split("/"));

/** A known API route template as is; anything else reduced like a path (`:id`). */
export function reduceApiRoute(apiRoute: string): string {
  return TEMPLATES.has(apiRoute) ? apiRoute : reducePath(apiRoute.split(/[?#]/)[0] ?? "");
}

/**
 * The API route template a path called matches (`/api/projects/7f3c…/documents` →
 * `/api/projects/:id/documents`), preferring the template with the most literal segments; a
 * path no template matches is reduced with {@link reducePath}.
 */
export function apiRouteOf(pathname: string): string {
  const segments = pathname.split(/[?#]/)[0]?.split("/") ?? [];
  let best: string[] | undefined;
  let bestLiterals = -1;
  for (const template of TEMPLATE_SEGMENTS) {
    if (template.length !== segments.length) {
      continue;
    }
    let literals = 0;
    let matches = true;
    for (const [i, part] of template.entries()) {
      if (part === ":id") {
        continue;
      }
      if (part !== segments[i]) {
        matches = false;
        break;
      }
      literals += 1;
    }
    if (matches && literals > bestLiterals) {
      best = template;
      bestLiterals = literals;
    }
  }
  return best ? best.join("/") : reducePath(segments.join("/"));
}

/** An identifier-shaped error name, else `Error` (the log-safety rule of `safe-error.ts`). */
export const reduceErrorName = safeErrorName;

/** The first line, log-safety redaction plus digit runs, capped at 200 characters. */
export function reduceErrorMessage(message: unknown): string | undefined {
  return safeMessage(message, { digitRuns: true });
}

/**
 * A frame's file: the path of one of the SPA's own assets, or `external` (another origin, an
 * extension, an inline script). `origin` is the page's: a full URL must be on it.
 */
export function reduceFrameFile(file: string, origin?: string): string {
  let path = file;
  if (/^[a-z][a-z0-9+.-]*:/i.test(file)) {
    try {
      const url = new URL(file);
      if (origin === undefined || url.origin !== origin) {
        return EXTERNAL_FRAME;
      }
      path = url.pathname;
    } catch {
      return EXTERNAL_FRAME;
    }
  }
  return OWN_ASSET.test(path) ? path : EXTERNAL_FRAME;
}

/** At most {@link CLIENT_ERROR_FRAMES} frames, each reduced with {@link reduceFrameFile}. */
export function reduceFrames(frames: readonly ClientFrame[], origin?: string): ClientFrame[] {
  return frames.slice(0, CLIENT_ERROR_FRAMES).map((f) => ({
    file: reduceFrameFile(f.file, origin),
    line: f.line,
    col: f.col,
  }));
}

/** `file:line:col` at the end of a V8 (`at f (…)`) or Gecko/WebKit (`f@…`) stack line. */
const STACK_LOCATION = /(?:^|[\s(@])([^\s()@]+):(\d+):(\d+)\)?$/;

/**
 * The frames of a stack: only each line's location (never a function name, never the message
 * lines that precede them), reduced to own assets or `external`.
 */
export function stackFramesOf(stack: unknown, origin?: string): ClientFrame[] {
  if (typeof stack !== "string") {
    return [];
  }
  const frames: ClientFrame[] = [];
  for (const raw of stack.slice(0, 8_000).split("\n")) {
    const line = raw.trim();
    // A V8 frame starts with `at `; a Gecko/WebKit one holds `@`. A message line has neither.
    if (!(line.startsWith("at ") || line.includes("@"))) {
      continue;
    }
    const match = STACK_LOCATION.exec(line);
    if (!match) {
      continue;
    }
    frames.push({
      file: reduceFrameFile(match[1] ?? "", origin),
      line: Math.min(Number(match[2]), 10_000_000),
      col: Math.min(Number(match[3]), 10_000_000),
    });
    if (frames.length === CLIENT_ERROR_FRAMES) {
      break;
    }
  }
  return frames;
}

/** A locale reduced to language and region (`fr-FR`), else undefined. */
export function reduceLocale(locale: unknown): string | undefined {
  if (typeof locale !== "string") {
    return undefined;
  }
  const [language, region] = locale.split(/[-_]/);
  const tag = region
    ? `${language?.toLowerCase()}-${region.toUpperCase()}`
    : language?.toLowerCase();
  return tag && LOCALE.test(tag) ? tag : undefined;
}

/** A build ID in the build-ID format, else undefined. */
export function reduceBuildId(build: unknown): string | undefined {
  return typeof build === "string" && CLIENT_BUILD_ID.test(build) ? build : undefined;
}

/** The duration class of a failed request. */
export function durationBucket(ms: number): DurationBucket {
  return ms < 1_000 ? "<1s" : ms < 5_000 ? "<5s" : ms < 30_000 ? "<30s" : ">=30s";
}

/** One event with every field reduced (the SPA before queuing, the API before recording). */
export function reduceClientEvent(event: ClientEvent, origin?: string): ClientEvent {
  const route = reduceRoute(event.route);
  switch (event.kind) {
    case "error": {
      const message = reduceErrorMessage(event.message);
      return {
        kind: "error",
        source: event.source,
        route,
        name: reduceErrorName(event.name),
        ...(message !== undefined && { message }),
        frames: reduceFrames(event.frames, origin),
        count: event.count,
      };
    }
    case "request":
      return { ...event, route, apiRoute: reduceApiRoute(event.apiRoute) };
    case "vital":
      return { ...event, route };
    case "boot":
      return { kind: "boot", route };
  }
}

/** The context with its locale reduced (the enums are already closed). */
export function reduceClientContext(context: ClientContext): ClientContext {
  const locale = reduceLocale(context.locale);
  return {
    browser: context.browser,
    ...(context.browserMajor !== undefined && { browserMajor: context.browserMajor }),
    os: context.os,
    ...(locale !== undefined && { locale }),
    viewport: context.viewport,
  };
}
